import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { NEWSLETTER_SERIES, getIssue, issuePath, sortedNewsletters } from "@/lib/newsletters";
import { CATEGORIES } from "@/lib/observatoire";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Overline } from "@/components/Reveal";
import { useSEO } from "@/lib/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


export default function Observatoire() {
  useSEO({
    title: "Observatoire HERUS",
    description:
      "L'Observatoire HERUS : analyses, notes de réflexion et études sur la gouvernance, les PPP & infrastructures, la transition énergétique, l'impact et le secteur public.",
    keywords: "observatoire, analyses, gouvernance, PPP, infrastructures, transition énergétique, impact, secteur public, bailleurs de fonds, HERUS Axiom",
    path: "/observatoire",
  });
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 400);
    return () => clearTimeout(t);
  }, [hash]);
  const [email, setEmail] = useState("");
  const [lang, setLang] = useState("fr");
  const [loading, setLoading] = useState(false);

  const subscribe = async (ev) => {
    ev.preventDefault();
    if (!email) return toast.error("Veuillez renseigner votre adresse email.");
    setLoading(true);
    try {
      await axios.post(`${API}/subscribers`, { email, lang });
      toast.success(lang === "en" ? "Thank you. You will receive our next issues in English." : "Merci. Vous recevrez nos prochains numéros par email.");
      setEmail("");
    } catch {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="observatoire-page" className="bg-[#F9F6F0]">
      {/* Hero */}
      <section className="pt-40 lg:pt-52 pb-16 mx-auto max-w-[1600px] px-6 lg:px-12">
        <Reveal><Overline>Observatoire HERUS</Overline></Reveal>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
          className="mt-8 font-serif-display font-light leading-[0.9] tracking-tighter text-[#0A0A0A] text-5xl sm:text-6xl lg:text-8xl max-w-5xl"
        >
          Analyses &
          <br />
          <span className="italic text-[#C5A059]">prises de position.</span>
        </motion.h1>
        <Reveal i={2}>
          <p className="mt-10 max-w-2xl text-[#0A0A0A]/70 text-base lg:text-lg font-light leading-relaxed">
            L'Observatoire HERUS rassemble nos analyses, notes de réflexion et décryptages consacrés à
            la gouvernance, aux partenariats public-privé, aux infrastructures, à la transition
            énergétique, à l'impact et au secteur public. Une contribution au débat des décideurs,
            fondée sur l'analyse et l'expérience.
          </p>
        </Reveal>

        {/* Category nav */}
        <Reveal i={3}>
          <div className="mt-10 flex flex-wrap gap-3">
            {CATEGORIES.map((c) => (
              <a
                key={c.id}
                href={`#${c.id}`}
                data-testid={`cat-link-${c.id}`}
                className="text-xs uppercase tracking-[0.2em] border border-[#0A0A0A]/20 px-4 py-2 text-[#0A0A0A]/70 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
              >
                {c.name}
              </a>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Newsletter issues */}
      <section data-testid="newsletter-issues" className="mx-auto max-w-[1600px] px-6 lg:px-12 py-14 border-t border-[#0A0A0A]/10">
        <Reveal className="flex flex-wrap items-center justify-between gap-4">
          <Overline>Newsletter · {NEWSLETTER_SERIES}</Overline>
          <Link to="/observatoire/newsletter" data-testid="newsletter-archive-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#0A0A0A]/60 hover:text-[#C5A059] transition-colors">
            Tous les numéros <ArrowUpRight size={14} />
          </Link>
        </Reveal>
        {sortedNewsletters().slice(0, 1).map((n) => (
          <Reveal key={n.slug} i={1} className="mt-10">
            <Link to={`/observatoire/${n.slug}`} data-testid={`newsletter-card-${n.slug}`} className="group grid lg:grid-cols-2 bg-[#0A0A0A] overflow-hidden">
              <div className="overflow-hidden aspect-video lg:aspect-auto">
                <img src={n.cover} alt={n.title} className="w-full h-full object-cover object-left transition-transform duration-[1.2s] group-hover:scale-105" />
              </div>
              <div className="p-8 lg:p-14 flex flex-col">
                <div className="flex justify-between text-xs uppercase tracking-[0.2em]">
                  <span className="text-[#C5A059]">{n.number} · {n.category}</span>
                  <span className="text-[#F9F6F0]/40">{n.date}</span>
                </div>
                <h3 className="mt-6 font-serif-display text-2xl lg:text-4xl font-light text-[#F9F6F0] leading-snug">{n.title}</h3>
                <p className="mt-5 text-[#F9F6F0]/60 font-light leading-relaxed flex-1">{n.excerpt}</p>
                <span className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C5A059]">
                  Lire le numéro
                  <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </span>
              </div>
            </Link>
            {getIssue(n.slug, "en") && (
              <Link to={issuePath(n.slug, "en")} data-testid={`newsletter-card-en-${n.slug}`} className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#0A0A0A]/60 hover:text-[#C5A059] transition-colors">
                English version <ArrowUpRight size={14} />
              </Link>
            )}
          </Reveal>
        ))}
      </section>

      {/* Categories */}
      {CATEGORIES.map((c) => (
        <section key={c.id} id={c.id} className="scroll-mt-28 mx-auto max-w-[1600px] px-6 lg:px-12 py-14 border-t border-[#0A0A0A]/10">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
              <div>
                <Overline>{c.name}</Overline>
                <p className="mt-3 text-[#0A0A0A]/60 font-light max-w-xl">{c.desc}</p>
              </div>
            </div>
          </Reveal>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.items.map((p, i) => (
              <Reveal key={i} i={i} className="group bg-[#0A0A0A] flex flex-col">
                <div className="relative bg-[#0A0A0A] h-48 p-8 flex flex-col justify-between overflow-hidden border-b border-white/10">
                  <div className="absolute inset-4 border border-[#C5A059]/30 pointer-events-none transition-colors duration-500 group-hover:border-[#C5A059]/70" />
                  <div className="relative flex items-center justify-between">
                    <span className="text-xs uppercase tracking-[0.2em] text-[#C5A059]">{p.type}</span>
                    <span className="text-xs uppercase tracking-[0.2em] text-[#F9F6F0]/40">{p.ref}</span>
                  </div>
                  <span className="relative font-axiom text-sm tracking-[0.3em] uppercase text-[#F9F6F0]/70">HERUS Axiom</span>
                </div>
                <div className="p-8 flex flex-col flex-1">
                  <span className="text-xs uppercase tracking-[0.2em] text-[#C5A059]">{p.date}</span>
                  <h3 className="mt-4 font-serif-display text-xl text-[#F9F6F0] leading-snug">{p.t}</h3>
                  <p className="mt-3 text-[#F9F6F0]/60 font-light leading-relaxed text-sm flex-1">{p.d}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F9F6F0]/40">Bientôt disponible</span>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      ))}

      {/* Newsletter */}
      <section id="abonnement" className="scroll-mt-28 mx-auto max-w-[1600px] px-6 lg:px-12 py-20">
        <Reveal className="border border-[#0A0A0A]/10 p-10 lg:p-16 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <div className="max-w-xl">
            <h3 className="font-serif-display text-3xl lg:text-4xl font-light text-[#0A0A0A]">
              Recevoir l'<span className="italic text-[#C5A059]">Observatoire.</span>
            </h3>
            <p className="mt-3 text-[#0A0A0A]/60 font-light">Recevez chaque numéro de la newsletter par email, en français ou en anglais.</p>
          </div>
          <form onSubmit={subscribe} data-testid="newsletter-form" className="flex flex-wrap items-center gap-4 w-full lg:w-auto">
            <div className="flex border border-[#0A0A0A]/15" role="group" aria-label="Langue">
              {[["fr", "FR"], ["en", "EN"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setLang(k)} data-testid={`newsletter-lang-${k}`} aria-pressed={lang === k} className={`px-3 py-2 text-xs tracking-[0.2em] transition-colors ${lang === k ? "bg-[#0A0A0A] text-[#C5A059]" : "text-[#0A0A0A]/50 hover:text-[#0A0A0A]"}`}>
                  {l}
                </button>
              ))}
            </div>
            <input
              data-testid="newsletter-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Votre email"
              className="flex-1 lg:w-72 bg-transparent border-b border-[#0A0A0A]/30 py-3 text-[#0A0A0A] placeholder:text-[#0A0A0A]/40 focus:border-[#C5A059] focus:outline-none transition-colors"
            />
            <button
              type="submit"
              data-testid="newsletter-submit"
              disabled={loading}
              className="group inline-flex items-center gap-2 bg-[#0A0A0A] text-[#F9F6F0] text-xs uppercase tracking-[0.2em] px-6 py-4 hover:bg-[#C5A059] hover:text-[#0A0A0A] transition-colors duration-500 disabled:opacity-60"
            >
              S'inscrire
              <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
